import React, { useState, useCallback } from "react";
import Layout from "../components/Layout";
import config from "../config";
import "./VectorSearch.css";

const VectorSearch = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [thumbnails, setThumbnails] = useState({});

  const fetchThumbnail = useCallback(async (driveFileId) => {
    try {
      const res = await fetch(
        `${config.api.baseUrl}/drive-vectordb/thumbnail/${driveFileId}`,
        { credentials: "include" }
      );
      const data = await res.json();
      if (data.success && data.thumbnail_url) {
        setThumbnails((prev) => ({ ...prev, [driveFileId]: data.thumbnail_url }));
      }
    } catch {
      // Thumbnail not available, that's fine
    }
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const res = await fetch(`${config.api.baseUrl}/drive-vectordb/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ query: query.trim(), limit: 20 }),
      });

      const data = await res.json();

      if (!data.success) {
        setError(data.error || "Search failed");
        setResults([]);
        return;
      }

      setResults(data.results || []);

      // Fetch thumbnails for image/video results
      for (const r of data.results || []) {
        if (isMediaFile(r.mime_type)) {
          fetchThumbnail(r.drive_file_id);
        }
      }
    } catch (err) {
      setError(err.message || "Network error");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const isMediaFile = (mimeType) => {
    return (
      mimeType?.startsWith("image/") || mimeType?.startsWith("video/")
    );
  };

  const isPdf = (mimeType) => {
    return mimeType === "application/pdf" || mimeType?.includes("pdf");
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith("image/")) return "🖼️";
    if (mimeType?.startsWith("video/")) return "🎬";
    if (mimeType?.startsWith("audio/")) return "🎵";
    if (isPdf(mimeType)) return "📄";
    if (mimeType?.includes("spreadsheet") || mimeType?.includes("excel"))
      return "📊";
    if (mimeType?.includes("presentation") || mimeType?.includes("powerpoint"))
      return "📽️";
    if (mimeType?.includes("document") || mimeType?.includes("word"))
      return "📝";
    if (mimeType?.startsWith("text/")) return "📃";
    return "📁";
  };

  const getScoreColor = (score) => {
    if (score >= 0.8) return "var(--vs-score-high)";
    if (score >= 0.5) return "var(--vs-score-mid)";
    return "var(--vs-score-low)";
  };

  const truncateText = (text, maxLen = 250) => {
    if (!text || text.startsWith("[Binary content:")) return null;
    if (text.length <= maxLen) return text;
    return text.substring(0, maxLen) + "...";
  };

  return (
    <Layout>
      <div className="vs-container">
        <div className="vs-header">
          <h1 className="vs-title">Drive Vector Search</h1>
          <p className="vs-subtitle">
            Search across your Google Drive files using AI-powered semantic search
          </p>
        </div>

        <form className="vs-search-form" onSubmit={handleSearch}>
          <div className="vs-search-bar">
            <input
              type="text"
              className="vs-search-input"
              placeholder="Search for documents, images, videos..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              disabled={loading}
            />
            <button
              type="submit"
              className="vs-search-btn"
              disabled={loading || !query.trim()}
            >
              {loading ? (
                <span className="vs-spinner" />
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              )}
              Search
            </button>
          </div>
        </form>

        {error && (
          <div className="vs-error">
            <span>Error: {error}</span>
          </div>
        )}

        {hasSearched && !loading && results.length === 0 && !error && (
          <div className="vs-empty">
            <p>No results found for "{query}"</p>
          </div>
        )}

        {results.length > 0 && (
          <div className="vs-results">
            <p className="vs-results-count">
              {results.length} result{results.length !== 1 ? "s" : ""} found
            </p>
            <div className="vs-results-grid">
              {results.map((result, index) => (
                <a
                  key={`${result.drive_file_id}-${result.chunk_index}-${index}`}
                  href={result.drive_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vs-card"
                >
                  {/* Thumbnail for media files */}
                  {isMediaFile(result.mime_type) && (
                    <div className="vs-card-thumbnail">
                      {thumbnails[result.drive_file_id] ? (
                        <img
                          src={thumbnails[result.drive_file_id]}
                          alt={result.file_name}
                          className="vs-thumbnail-img"
                        />
                      ) : (
                        <div className="vs-thumbnail-placeholder">
                          <span className="vs-file-icon-large">
                            {getFileIcon(result.mime_type)}
                          </span>
                        </div>
                      )}
                      {result.mime_type?.startsWith("video/") && (
                        <div className="vs-video-badge">VIDEO</div>
                      )}
                    </div>
                  )}

                  <div className="vs-card-body">
                    <div className="vs-card-header">
                      <span className="vs-file-icon">
                        {getFileIcon(result.mime_type)}
                      </span>
                      <div className="vs-card-title-group">
                        <h3 className="vs-card-title">{result.file_name}</h3>
                        <p className="vs-card-path">{result.file_path}</p>
                      </div>
                      <div
                        className="vs-score-badge"
                        style={{ backgroundColor: getScoreColor(result.score) }}
                      >
                        {(result.score * 100).toFixed(1)}%
                      </div>
                    </div>

                    {/* Text preview for text/PDF content */}
                    {truncateText(result.text) && (
                      <p className="vs-card-preview">
                        {truncateText(result.text)}
                      </p>
                    )}

                    <div className="vs-card-footer">
                      <span className="vs-card-mime">{result.mime_type}</span>
                      {result.chunk_index > 0 && (
                        <span className="vs-card-chunk">
                          Chunk {result.chunk_index + 1}
                        </span>
                      )}
                      <span className="vs-card-link-text">
                        Open in Drive →
                      </span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default VectorSearch;
